import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ARENA_SIZE, PLAYER_COLORS } from '../../../utils/constants.js';
import { colored, vary, seededRandom, fbm, noise2, jitter, Fader } from '../../Style.js';
import { SCIFI, metal, glow, halo } from './style.js';
import { Obstacle } from './Obstacle.js';

const HALF = ARENA_SIZE / 2;
const WORLD_SIZE = 1200;
const CITY_INNER = 430;
const CITY_OUTER = 640;
const WALL_HEIGHT = 5;
const WALL_THICKNESS = 1.5;
const PLINTH_HEIGHT = 1.3;

// Guide lanes on the deck: from each base to the centre hub, and from the hub
// out to the foot of the ramps up to the forward platforms. Light flows along
// each lane from its first point to its last.
const LANES = [
  { from: [-77, 0], to: [-7, 0], team: 0 },
  { from: [77, 0], to: [7, 0], team: 1 },
  { from: [0, -7], to: [0, -47] },
  { from: [0, 7], to: [0, 47] }
];
const LANE_WIDTH = 3.2;

function smoothstep(a, b, x) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

// Height of the land outside the barrier. The arena deck itself stays flat
// because the server's movement and line of sight assume flat ground.
export function terrainHeight(x, z) {
  const edge = Math.max(Math.abs(x), Math.abs(z)) - HALF;
  if (edge < 3) return -0.3;

  const r = Math.hypot(x, z);
  const rise = smoothstep(3, 90, edge);
  let h = rise * (3 + fbm(x / 80, z / 80) * 7 + Math.max(0, edge - 30) * 0.03);

  // Flat-topped mesas, kept out of the north so the skyline shows behind the arena
  const south = smoothstep(-0.55, 0.35, z / Math.max(r, 1));
  const band = smoothstep(150, 230, r) * (1 - smoothstep(CITY_INNER - 60, CITY_INNER - 10, r));
  const m = fbm(x / 130 + 7, z / 130 - 3, 3);
  const mesa = smoothstep(0.08, 0.16, m) * band * (0.25 + south * 0.75);
  if (mesa > 0) {
    // Terraced cliffs: quantise the cap so the sides step down in ledges
    const cap = 30 + fbm(x / 60, z / 60, 2) * 18 + south * 25;
    const terraced = Math.floor(mesa * 4) / 4 * 0.35 + mesa * 0.65;
    h += terraced * cap;
  }

  // Level ground for the city
  const city = smoothstep(CITY_INNER - 40, CITY_INNER, r);
  return h * (1 - city) + (2 + noise2(x / 50, z / 50) * 0.5) * city;
}

// Soft round points with per-point colour, used for lights, traffic and motes.
// Positions can be animated in the vertex shader via `motion`.
function pointsMaterial(uniforms, size, motion = '', fade = '1.0') {
  return new THREE.ShaderMaterial({
    uniforms: { ...uniforms, uSize: { value: size } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: /* glsl */`
      uniform float uTime;
      uniform float uSize;
      attribute vec3 aColor;
      attribute vec4 aData;
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        vec3 p = position;
        float alpha = 1.0;
        ${motion}
        vColor = aColor;
        vAlpha = alpha * ${fade};
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = uSize * (300.0 / max(-mv.z, 1.0));
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      varying vec3 vColor;
      varying float vAlpha;
      void main() {
        float d = length(gl_PointCoord - 0.5);
        float a = smoothstep(0.5, 0.0, d);
        a = a * a;
        gl_FragColor = vec4(vColor * a * vAlpha, 1.0);
      }
    `
  });
}

// A barrier side: a metal plinth carrying a translucent force field. It
// exposes the same occluder API as an Obstacle so it fades out of the camera's way.
class BarrierWall {
  constructor(scene, data, fieldMaterial) {
    this.scene = scene;
    this.data = data;
    this.mesh = new THREE.Group();
    const { position, size } = data;
    const alongX = size.x >= size.z;
    const length = alongX ? size.x : size.z;
    const ry = alongX ? 0 : Math.PI / 2;
    const rand = seededRandom(length * 7 + position.x + position.z * 3);
    const parts = [];
    const lights = [];
    const add = (list, geo, color, x, y, z) => list.push(colored(geo, color, { x, y, z }).rotateY(ry));

    add(parts, new THREE.BoxGeometry(length, 0.4, WALL_THICKNESS + 0.6), SCIFI.alloyDark, 0, 0.2, 0);
    add(parts, new THREE.BoxGeometry(length, PLINTH_HEIGHT - 0.4, WALL_THICKNESS), SCIFI.alloyDeep, 0, 0.4 + (PLINTH_HEIGHT - 0.4) / 2, 0);
    add(parts, new THREE.BoxGeometry(length, 0.16, WALL_THICKNESS + 0.2), SCIFI.alloyLight, 0, PLINTH_HEIGHT, 0);

    // Emitter posts every few metres, with plates between them
    const bays = Math.max(2, Math.round(length / 12));
    const bay = length / bays;
    for (let i = 0; i <= bays; i++) {
      const x = -length / 2 + bay * i;
      add(parts, new THREE.BoxGeometry(0.9, WALL_HEIGHT + 0.4, 0.9), SCIFI.alloyDark, x, (WALL_HEIGHT + 0.4) / 2, 0);
      add(parts, new THREE.BoxGeometry(1.2, 0.3, 1.2), SCIFI.alloy, x, WALL_HEIGHT + 0.5, 0);
      add(lights, new THREE.BoxGeometry(0.3, WALL_HEIGHT - PLINTH_HEIGHT - 0.6, 0.95), SCIFI.cyan, x, PLINTH_HEIGHT + (WALL_HEIGHT - PLINTH_HEIGHT) / 2, 0);
      add(lights, new THREE.BoxGeometry(0.5, 0.12, 1.25), SCIFI.cyan, x, WALL_HEIGHT + 0.7, 0);
      if (i < bays) {
        for (const side of [-1, 1]) {
          add(parts, new THREE.BoxGeometry(bay - 1.4, PLINTH_HEIGHT - 0.6, 0.1), vary(SCIFI.alloy, rand, 0.08), x + bay / 2, 0.75, side * (WALL_THICKNESS / 2 + 0.05));
        }
      }
    }
    for (const side of [-1, 1]) {
      add(lights, new THREE.BoxGeometry(length, 0.08, 0.06), SCIFI.cyan, 0, PLINTH_HEIGHT - 0.12, side * (WALL_THICKNESS / 2 + 0.11));
    }

    const body = new THREE.Mesh(mergeGeometries(parts), metal(0xffffff, { vertexColors: true }));
    body.castShadow = true;
    body.receiveShadow = true;
    this.mesh.add(body);
    this.mesh.add(new THREE.Mesh(mergeGeometries(lights), glow(0xffffff, { vertexColors: true, boost: 1.6 })));

    // The field itself is light: it is already see-through and is never faded
    const fieldHeight = WALL_HEIGHT - PLINTH_HEIGHT + 0.5;
    const field = new THREE.Mesh(new THREE.PlaneGeometry(length, fieldHeight).rotateY(ry), fieldMaterial);
    field.position.y = PLINTH_HEIGHT + fieldHeight / 2;
    field.userData.noFade = true;
    field.renderOrder = 2;
    this.mesh.add(field);

    this.mesh.position.set(position.x, 0, position.z);
    scene.add(this.mesh);
    this.mesh.updateMatrixWorld(true);
    this.fader = new Fader(this.mesh);
  }

  setFadeTarget(opacity) {
    this.fader.target = opacity;
  }

  update(deltaTime) {
    this.fader.update(deltaTime);
  }
}

// The world around and beneath the fighting: night sky, ringed planet, mesas,
// a distant city with traffic and airships, and the arena's alloy deck.
export class Arena {
  constructor(scene) {
    this.scene = scene;
    this.rand = seededRandom(2077);
    this.boundary = [];
    this.uniforms = { uTime: { value: 0 } };
    this.followers = [];
    this.airships = [];
    this.create();
  }

  create() {
    this.createSky();
    this.createPlanet();
    this.createPlayfield();
    this.createLanes();
    this.createTerrain();
    this.createRocks();
    this.createCity();
    this.createTraffic();
    this.createAirships();
    this.createWalls();
    this.createMotes();
  }

  createSky() {
    this.skyUniforms = {
      uTime: this.uniforms.uTime,
      topColor: { value: new THREE.Color(0x03050f) },
      midColor: { value: new THREE.Color(0x0d1440) },
      horizonA: { value: new THREE.Color(0x3a2a7a) },
      horizonB: { value: new THREE.Color(0x1e3f78) },
      groundColor: { value: new THREE.Color(0x141a36) },
      moonDirection: { value: new THREE.Vector3(0, 1, 0) },
      aurora: { value: 0.6 },
      hueShift: { value: 0 }
    };

    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(1500, 48, 24),
      new THREE.ShaderMaterial({
        uniforms: this.skyUniforms,
        side: THREE.BackSide,
        depthWrite: false,
        fog: false,
        vertexShader: /* glsl */`
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          uniform float uTime;
          uniform vec3 topColor;
          uniform vec3 midColor;
          uniform vec3 horizonA;
          uniform vec3 horizonB;
          uniform vec3 groundColor;
          uniform vec3 moonDirection;
          uniform float aurora;
          uniform float hueShift;
          varying vec3 vDir;

          float hash(vec3 p) {
            return fract(sin(dot(p, vec3(12.9898, 78.233, 45.164))) * 43758.5453);
          }
          float hash2(vec2 p) {
            return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
          }
          float vnoise(vec2 p) {
            vec2 i = floor(p);
            vec2 f = fract(p);
            vec2 u = f * f * (3.0 - 2.0 * f);
            return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), u.x),
                       mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), u.x), u.y);
          }

          void main() {
            vec3 dir = normalize(vDir);
            float h = dir.y;
            vec3 horizon = mix(horizonA, horizonB, hueShift);
            vec3 col = mix(horizon, midColor, smoothstep(0.0, 0.25, h));
            col = mix(col, topColor, smoothstep(0.2, 0.85, h));
            col = mix(col, groundColor, smoothstep(0.0, -0.12, h));

            // Light pollution from the city ring, brightest just above the skyline
            col += vec3(0.16, 0.12, 0.3) * exp(-abs(h - 0.02) * 22.0) * 0.8;

            // Two layers of stars that twinkle gently
            vec3 cell = floor(dir * 320.0);
            float s1 = step(0.9965, hash(cell));
            float tw = 0.65 + 0.35 * sin(uTime * (1.5 + hash(cell + 3.0) * 3.0) + hash(cell) * 40.0);
            vec3 cell2 = floor(dir * 900.0);
            float s2 = step(0.9985, hash(cell2)) * 0.5;
            float starMask = smoothstep(0.04, 0.3, h);
            col += vec3(0.85, 0.9, 1.0) * (s1 * tw + s2) * starMask;

            // A faint band of galaxy dust across the sky
            float band = exp(-pow(dot(dir, normalize(vec3(0.3, 0.5, 0.8))) * 3.0, 2.0));
            col += vec3(0.12, 0.1, 0.22) * band * vnoise(dir.xz * 9.0 + dir.y * 4.0) * starMask;

            // Aurora curtains to the north, shimmering slowly
            float az = atan(dir.x, -dir.z);
            float curtain = vnoise(vec2(az * 6.0 + uTime * 0.05, uTime * 0.03)) * 0.7
              + vnoise(vec2(az * 17.0 - uTime * 0.11, 3.0)) * 0.3;
            float rays = 0.6 + 0.4 * vnoise(vec2(az * 60.0, uTime * 0.2));
            float north = smoothstep(0.2, -0.7, dir.z);
            float lower = smoothstep(0.06, 0.16, h) * (1.0 - smoothstep(0.16, 0.5, h - curtain * 0.12));
            vec3 auroraCol = mix(vec3(0.1, 0.95, 0.75), vec3(0.55, 0.3, 1.0), smoothstep(0.12, 0.42, h));
            col += auroraCol * smoothstep(0.35, 0.8, curtain) * rays * lower * north * aurora * 0.55;

            // The moon: a pale disc with soft maria and a wide glow
            float d = max(dot(dir, moonDirection), 0.0);
            vec3 moonCol = vec3(0.86, 0.9, 1.0);
            float disc = smoothstep(0.99935, 0.99955, d);
            float maria = vnoise(dir.xy * 900.0) * 0.25 + vnoise(dir.zy * 400.0) * 0.15;
            col = mix(col, moonCol * (1.0 - maria), disc);
            col += moonCol * pow(d, 400.0) * 0.35 + moonCol * pow(d, 24.0) * 0.08;

            gl_FragColor = vec4(col, 1.0);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      })
    );
    sky.renderOrder = -2;
    sky.frustumCulled = false;
    this.sky = sky;
    this.scene.add(sky);
    this.followers.push({ object: sky, offset: new THREE.Vector3() });
  }

  // A banded gas giant with a tilted ring, low over the eastern horizon.
  createPlanet() {
    const lightDir = new THREE.Vector3(-0.8, 0.35, 0.5).normalize();
    const planetUniforms = { lightDir: { value: lightDir } };
    const planet = new THREE.Mesh(
      new THREE.SphereGeometry(150, 48, 32),
      new THREE.ShaderMaterial({
        uniforms: planetUniforms,
        fog: false,
        vertexShader: /* glsl */`
          varying vec3 vNormal;
          varying vec3 vLocal;
          void main() {
            vNormal = normalize(mat3(modelMatrix) * normal);
            vLocal = position / 150.0;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          uniform vec3 lightDir;
          varying vec3 vNormal;
          varying vec3 vLocal;
          void main() {
            float lat = vLocal.y;
            float bands = sin(lat * 22.0 + sin(lat * 7.0) * 1.5) * 0.5 + 0.5;
            vec3 col = mix(vec3(0.42, 0.3, 0.62), vec3(0.85, 0.62, 0.55), bands * 0.6);
            col = mix(col, vec3(0.3, 0.45, 0.75), smoothstep(0.6, 0.95, abs(lat)));
            float lit = smoothstep(-0.15, 0.6, dot(normalize(vNormal), lightDir));
            float rim = pow(1.0 - abs(normalize(vNormal).z), 3.0);
            vec3 outCol = col * (0.04 + lit * 0.85) + vec3(0.35, 0.3, 0.8) * rim * 0.25;
            gl_FragColor = vec4(outCol, 1.0);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      })
    );

    const ring = new THREE.Mesh(
      new THREE.RingGeometry(200, 330, 128, 1),
      new THREE.ShaderMaterial({
        uniforms: planetUniforms,
        side: THREE.DoubleSide,
        transparent: true,
        depthWrite: false,
        fog: false,
        vertexShader: /* glsl */`
          varying vec2 vPos;
          void main() {
            vPos = position.xy;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          varying vec2 vPos;
          float hash(float x) { return fract(sin(x * 91.7) * 43758.5453); }
          void main() {
            float r = (length(vPos) - 200.0) / 130.0;
            float band = hash(floor(r * 40.0)) * 0.6 + hash(floor(r * 9.0) + 7.0) * 0.4;
            float gap = smoothstep(0.52, 0.55, r) * (1.0 - smoothstep(0.58, 0.61, r));
            float alpha = band * (1.0 - gap) * smoothstep(0.0, 0.08, r) * (1.0 - smoothstep(0.9, 1.0, r)) * 0.55;
            vec3 col = mix(vec3(0.75, 0.65, 0.85), vec3(0.95, 0.8, 0.65), band);
            gl_FragColor = vec4(col * 0.75, alpha);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      })
    );
    ring.rotation.set(-Math.PI / 2 + 0.35, 0.25, 0.4);

    const group = new THREE.Group();
    group.add(planet, ring);
    group.scale.setScalar(0.8);
    group.renderOrder = -1;
    planet.renderOrder = -1;
    ring.renderOrder = -1;
    this.scene.add(group);
    this.followers.push({ object: group, offset: new THREE.Vector3(1050, 330, -520) });
  }

  // The deck: brushed alloy hex plates with faint glowing seams and a slow
  // pulse rippling out from the centre.
  createPlayfield() {
    const size = ARENA_SIZE + 4;
    const geometry = new THREE.PlaneGeometry(size, size, 1, 1);
    geometry.rotateX(-Math.PI / 2);

    const material = new THREE.MeshStandardMaterial({
      color: 0x4a4f58,
      roughness: 0.62,
      metalness: 0.4
    });
    const seamColor = { value: new THREE.Color(SCIFI.cyanSoft) };
    material.onBeforeCompile = shader => {
      shader.uniforms.uTime = this.uniforms.uTime;
      shader.uniforms.uSeamColor = seamColor;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vFloorPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvFloorPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', /* glsl */`#include <common>
          uniform float uTime;
          uniform vec3 uSeamColor;
          varying vec3 vFloorPos;
          float floorHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          // x: distance to the nearest hex edge (0 on the seam); yz: cell id
          vec3 hexCell(vec2 p) {
            const vec2 r = vec2(1.0, 1.7320508);
            vec2 h = r * 0.5;
            vec2 a = mod(p, r) - h;
            vec2 b = mod(p - h, r) - h;
            vec2 g = dot(a, a) < dot(b, b) ? a : b;
            vec2 q = abs(g);
            float d = max(dot(q, vec2(0.5, 0.8660254)), q.x);
            return vec3(0.5 - d, p - g);
          }
        `)
        .replace('#include <color_fragment>', /* glsl */`#include <color_fragment>
          vec3 hc = hexCell(vFloorPos.xz / 6.0);
          float tile = floorHash(hc.yz);
          float hw = fwidth(hc.x);
          float seam = 1.0 - smoothstep(0.0, 0.03 + hw, hc.x);
          float brush = floorHash(vec2(floor(vFloorPos.z * 5.0 + tile * 9.0), tile));
          diffuseColor.rgb *= (0.84 + tile * 0.26) * (0.96 + brush * 0.07) * (1.0 - seam * 0.6);
        `)
        .replace('#include <emissivemap_fragment>', /* glsl */`#include <emissivemap_fragment>
          float wave = 0.5 + 0.5 * sin(length(vFloorPos.xz) * 0.07 - uTime * 1.2);
          float line = 1.0 - smoothstep(0.0, 0.012 + hw * 1.2, hc.x);
          totalEmissiveRadiance += uSeamColor * line * (0.05 + wave * wave * 0.22);
          float lit = step(0.982, tile) * (0.55 + 0.45 * sin(uTime * 1.7 + tile * 60.0));
          totalEmissiveRadiance += uSeamColor * lit * 0.07 * (1.0 - seam);
        `);
    };
    material.customProgramCacheKey = () => 'scifi-deck';

    const ground = new THREE.Mesh(geometry, material);
    ground.receiveShadow = true;
    this.ground = ground;
    this.scene.add(ground);
  }

  // Glowing guide lanes with chevrons flowing along them.
  createLanes() {
    const positions = [];
    const along = [];
    const across = [];
    const colors = [];
    const indices = [];
    const neutral = new THREE.Color(SCIFI.cyan);
    const segmentsPerUnit = 0.25;

    for (const lane of LANES) {
      const [ax, az] = lane.from;
      const [bx, bz] = lane.to;
      const length = Math.hypot(bx - ax, bz - az);
      const dx = (bx - ax) / length;
      const dz = (bz - az) / length;
      // Perpendicular for the lane's width
      const px = -dz;
      const pz = dx;
      const team = lane.team !== undefined ? new THREE.Color(PLAYER_COLORS[lane.team]) : null;
      const steps = Math.max(2, Math.ceil(length * segmentsPerUnit));
      const start = positions.length / 3;
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const cx = ax + (bx - ax) * t;
        const cz = az + (bz - az) * t;
        // Team lanes start in the team's colour and cool to cyan by the centre
        const c = team ? team.clone().lerp(neutral, smoothstep(0.1, 0.8, t)) : neutral;
        for (const side of [-1, 1]) {
          positions.push(cx + px * side * LANE_WIDTH / 2, 0.035, cz + pz * side * LANE_WIDTH / 2);
          along.push(t * length);
          across.push(side);
          colors.push(c.r, c.g, c.b);
        }
        if (i < steps) {
          const k = start + i * 2;
          indices.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
        }
      }
    }

    // Hub ring around the centre where the lanes meet
    const hubSegments = 64;
    const hubStart = positions.length / 3;
    for (let i = 0; i <= hubSegments; i++) {
      const a = (i / hubSegments) * Math.PI * 2;
      for (const [r, side] of [[5.4, -1], [7.0, 1]]) {
        positions.push(Math.cos(a) * r, 0.035, Math.sin(a) * r);
        along.push(-1000 + i);
        across.push(side);
        colors.push(neutral.r, neutral.g, neutral.b);
      }
      if (i < hubSegments) {
        const k = hubStart + i * 2;
        indices.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
      }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute('aAlong', new THREE.Float32BufferAttribute(along, 1));
    geometry.setAttribute('aAcross', new THREE.Float32BufferAttribute(across, 1));
    geometry.setAttribute('aColor', new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(indices);

    const material = new THREE.ShaderMaterial({
      uniforms: { uTime: this.uniforms.uTime },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      vertexShader: /* glsl */`
        attribute float aAlong;
        attribute float aAcross;
        attribute vec3 aColor;
        varying float vAlong;
        varying float vAcross;
        varying vec3 vColor;
        void main() {
          vAlong = aAlong;
          vAcross = aAcross;
          vColor = aColor;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        uniform float uTime;
        varying float vAlong;
        varying float vAcross;
        varying vec3 vColor;
        void main() {
          float x = abs(vAcross);
          float aw = fwidth(vAcross) * 1.5;
          if (vAlong < -500.0) {
            // Hub ring: a thin double line with a slow rotating dash
            float edge = smoothstep(0.75 - aw, 0.85, x) + (1.0 - smoothstep(0.12, 0.12 + aw, x)) * 0.5;
            float dash = step(0.5, fract((vAlong + 1000.0) / 4.0 - uTime * 0.15));
            gl_FragColor = vec4(vColor * (edge * 0.8 + dash * 0.12), 1.0);
            return;
          }
          // Edge rails
          float rails = smoothstep(0.8 - aw, 0.9, x) * (1.0 - smoothstep(0.97, 1.0, x));
          // Chevrons flowing along the lane
          float c = fract((vAlong - x * 1.1) / 5.0 - uTime * 0.45);
          float chevron = smoothstep(0.0, 0.05, c) * (1.0 - smoothstep(0.14, 0.2, c)) * (1.0 - smoothstep(0.62, 0.72, x));
          // Faint fill so the lane reads as a surface
          float fill = 0.06;
          float fadeIn = smoothstep(0.0, 3.0, vAlong);
          gl_FragColor = vec4(vColor * (rails * 0.85 + chevron * 0.7 + fill) * fadeIn, 1.0);
        }
      `
    });
    this.lanes = new THREE.Mesh(geometry, material);
    this.lanes.renderOrder = 1;
    this.scene.add(this.lanes);
  }

  // Dark desert plain rising to terraced mesas, flattening out for the city.
  createTerrain() {
    const segments = 150;
    const geometry = new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE, segments, segments);
    geometry.rotateX(-Math.PI / 2);
    const pos = geometry.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, terrainHeight(pos.getX(i), pos.getZ(i)));
    }

    const flat = geometry.toNonIndexed();
    flat.computeVertexNormals();
    const fpos = flat.attributes.position;
    const normals = flat.attributes.normal;
    const colors = new Float32Array(fpos.count * 3);
    const rand = seededRandom(31);
    const c = new THREE.Color();
    const low = new THREE.Color(0x1a1a20);
    const high = new THREE.Color(0x2c2a31);
    const cliff = new THREE.Color(0x1f1d24);
    const sand = new THREE.Color(0x24232a);

    for (let i = 0; i < fpos.count; i += 3) {
      const cx = (fpos.getX(i) + fpos.getX(i + 1) + fpos.getX(i + 2)) / 3;
      const cy = (fpos.getY(i) + fpos.getY(i + 1) + fpos.getY(i + 2)) / 3;
      const cz = (fpos.getZ(i) + fpos.getZ(i + 1) + fpos.getZ(i + 2)) / 3;
      const steep = 1 - normals.getY(i);
      if (steep > 0.3) {
        c.copy(cliff).offsetHSL(0, 0, Math.sin(cy * 0.9) * 0.02);
      } else if (cy > 20) {
        c.copy(high);
      } else {
        c.copy(low).lerp(sand, fbm(cx / 40, cz / 40) * 0.5 + 0.5);
      }
      c.offsetHSL(0, 0, (rand() - 0.5) * 0.025);
      for (let k = 0; k < 3; k++) {
        colors[(i + k) * 3] = c.r;
        colors[(i + k) * 3 + 1] = c.g;
        colors[(i + k) * 3 + 2] = c.b;
      }
    }
    flat.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const terrain = new THREE.Mesh(flat, new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 0.95,
      metalness: 0
    }));
    terrain.receiveShadow = true;
    this.scene.add(terrain);
  }

  // Scattered boulders and a few comm masts on the plain.
  createRocks() {
    const rand = this.rand;
    const dummy = new THREE.Object3D();

    const rock = colored(jitter(new THREE.DodecahedronGeometry(1, 0), 0.45, rand), 0xffffff, { y: 0.2, scale: [1.3, 0.8, 1] });

    const scatter = (geometry, count, accept, minScale, maxScale, tint) => {
      const mesh = new THREE.InstancedMesh(geometry, metal(0xffffff, { vertexColors: true, roughness: 0.95, metalness: 0, flat: true }), count);
      let placed = 0;
      for (let attempt = 0; placed < count && attempt < count * 30; attempt++) {
        const x = (rand() - 0.5) * WORLD_SIZE * 0.85;
        const z = (rand() - 0.5) * WORLD_SIZE * 0.85;
        const h = terrainHeight(x, z);
        if (!accept(x, z, h)) continue;
        const s = minScale + rand() * (maxScale - minScale);
        dummy.position.set(x, h - 0.3, z);
        dummy.rotation.set((rand() - 0.5) * 0.2, rand() * Math.PI * 2, (rand() - 0.5) * 0.2);
        dummy.scale.set(s, s * (0.8 + rand() * 0.5), s);
        dummy.updateMatrix();
        mesh.setMatrixAt(placed, dummy.matrix);
        mesh.setColorAt(placed, vary(tint, rand, 0.06));
        placed++;
      }
      mesh.count = placed;
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
    };

    const outside = (x, z, margin) => Math.max(Math.abs(x), Math.abs(z)) > HALF + margin;
    const r = (x, z) => Math.hypot(x, z);
    scatter(rock, 420, (x, z) => outside(x, z, 6) && r(x, z) < CITY_INNER - 30, 0.6, 4.5, 0x2c2a32);

    // Comm masts with a blinking red light on top
    const masts = [];
    this.mastLights = [];
    for (let i = 0; i < 26; i++) {
      const a = rand() * Math.PI * 2;
      const d = 130 + rand() * 260;
      const x = Math.cos(a) * d;
      const z = Math.sin(a) * d;
      if (!outside(x, z, 20)) continue;
      const h = terrainHeight(x, z);
      const height = 14 + rand() * 18;
      masts.push(colored(new THREE.CylinderGeometry(0.25, 0.6, height, 4), SCIFI.alloyDark, { x, y: h + height / 2, z }));
      masts.push(colored(new THREE.BoxGeometry(2.4, 0.2, 0.2), SCIFI.alloy, { x, y: h + height * 0.75, z, ry: a }));
      this.mastLights.push([x, h + height + 0.4, z]);
    }
    const mastMesh = new THREE.Mesh(mergeGeometries(masts), metal(0xffffff, { vertexColors: true }));
    this.scene.add(mastMesh);
  }

  // A ring of towers on the horizon, windows lit by shader, with roof beacons.
  createCity() {
    const rand = seededRandom(404);
    const count = 900;
    const box = new THREE.BoxGeometry(1, 1, 1).translate(0, 0.5, 0);
    const seeds = new Float32Array(count);
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: this.uniforms.uTime,
        uHaze: { value: new THREE.Color(0x1a1d44) },
        uBase: { value: new THREE.Color(0x0b0e1a) }
      },
      vertexShader: /* glsl */`
        attribute float aSeed;
        varying vec3 vWorld;
        varying vec3 vNormalW;
        varying float vSeed;
        varying float vTop;
        varying float vBottom;
        void main() {
          vec4 world = modelMatrix * instanceMatrix * vec4(position, 1.0);
          vWorld = world.xyz;
          vNormalW = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * normal);
          vSeed = aSeed;
          vBottom = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).y;
          vTop = (modelMatrix * instanceMatrix * vec4(0.0, 1.0, 0.0, 1.0)).y;
          gl_Position = projectionMatrix * viewMatrix * world;
        }
      `,
      fragmentShader: /* glsl */`
        uniform float uTime;
        uniform vec3 uHaze;
        uniform vec3 uBase;
        varying vec3 vWorld;
        varying vec3 vNormalW;
        varying float vSeed;
        varying float vTop;
        varying float vBottom;
        float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
        void main() {
          vec3 n = normalize(vNormalW);
          float height01 = (vWorld.y - vBottom) / max(vTop - vBottom, 1.0);
          vec3 col = uBase * (0.7 + height01 * 0.6);
          // Moonlit faces are a touch lighter
          col += vec3(0.03, 0.035, 0.06) * max(dot(n, normalize(vec3(-0.55, 0.62, 0.56))), 0.0);
          if (abs(n.y) < 0.5) {
            float u = abs(n.x) > 0.5 ? vWorld.z : vWorld.x;
            vec2 cell = vec2(u / 2.2, (vWorld.y - vBottom) / 3.0);
            vec2 id = floor(cell);
            vec2 f = fract(cell);
            float window = step(0.2, f.x) * step(f.x, 0.8) * step(0.28, f.y) * step(f.y, 0.78);
            float h = hash(id + vSeed * 31.7);
            float density = 0.35 + fract(vSeed * 7.3) * 0.4;
            float on = step(1.0 - density, h);
            // A few windows switch on and off over time
            on *= step(0.04, fract(h * 13.0 + floor(uTime * 0.05 + h * 10.0) * 0.37));
            vec3 warm = vec3(1.0, 0.76, 0.45);
            vec3 cool = vec3(0.5, 0.85, 1.0);
            vec3 wc = mix(warm, cool, step(0.6, hash(id.yx + vSeed)));
            col += wc * window * on * (0.45 + 0.55 * hash(id * 1.7 + 3.0));
            // Neon crown band on some towers
            float crown = step(vTop - 2.2, vWorld.y) * step(vWorld.y, vTop - 1.2) * step(0.62, fract(vSeed * 3.1));
            vec3 neon = mix(vec3(0.2, 0.9, 1.0), vec3(0.85, 0.35, 1.0), step(0.5, fract(vSeed * 11.0)));
            col += neon * crown * 1.4;
          }
          float dist = length(vWorld - cameraPosition);
          float haze = smoothstep(200.0, 1100.0, dist) * 0.6 + (1.0 - height01) * 0.2;
          col = mix(col, uHaze, clamp(haze, 0.0, 0.85));
          gl_FragColor = vec4(col, 1.0);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }
      `
    });

    const city = new THREE.InstancedMesh(box, material, count);
    const dummy = new THREE.Object3D();
    const beacons = [];
    const spires = [];
    // Downtown clusters, the largest to the north behind the default view
    const clusters = [
      { angle: -Math.PI / 2, spread: 0.6, boost: 1 },
      { angle: -Math.PI / 2 - 1.1, spread: 0.35, boost: 0.6 },
      { angle: 0.2, spread: 0.3, boost: 0.55 },
      { angle: Math.PI - 0.3, spread: 0.3, boost: 0.5 },
      { angle: Math.PI / 2 + 0.6, spread: 0.3, boost: 0.4 }
    ];
    let placed = 0;
    for (let attempt = 0; placed < count && attempt < count * 4; attempt++) {
      const angle = rand() * Math.PI * 2;
      const radius = CITY_INNER + 10 + Math.pow(rand(), 0.7) * (CITY_OUTER - CITY_INNER);
      let boost = 0;
      for (const cl of clusters) {
        let d = Math.abs(angle - ((cl.angle + Math.PI * 4) % (Math.PI * 2)));
        d = Math.min(d, Math.PI * 2 - d);
        boost = Math.max(boost, cl.boost * Math.exp(-(d * d) / (cl.spread * cl.spread)));
      }
      if (rand() > 0.25 + boost) continue;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const w = 8 + rand() * 14;
      const d = 8 + rand() * 14;
      const height = 15 + rand() * 35 + boost * Math.pow(rand(), 1.5) * 170;
      dummy.position.set(x, 1, z);
      dummy.rotation.set(0, -angle + (rand() < 0.3 ? Math.PI / 4 : 0), 0);
      dummy.scale.set(w, height, d);
      dummy.updateMatrix();
      city.setMatrixAt(placed, dummy.matrix);
      seeds[placed] = rand();
      if (height > 70) {
        beacons.push([x, height + 1.5, z]);
        // Slim spire on the tallest towers
        if (height > 110) {
          spires.push(colored(new THREE.CylinderGeometry(0.4, 1.4, 14, 4), 0x10131f, { x, y: height + 8, z }));
          beacons.push([x, height + 15.5, z]);
        }
      }
      placed++;
    }
    city.count = placed;
    box.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    city.frustumCulled = false;
    this.scene.add(city);

    if (spires.length) {
      this.scene.add(new THREE.Mesh(mergeGeometries(spires), glow(0xffffff, { vertexColors: true })));
    }

    // Blinking beacons on roofs, spires and masts, each on its own rhythm
    const all = [...beacons, ...this.mastLights];
    const positions = new Float32Array(all.length * 3);
    const colors = new Float32Array(all.length * 3);
    const data = new Float32Array(all.length * 4);
    const red = new THREE.Color(0xff3030);
    const white = new THREE.Color(0xffe8e8);
    all.forEach(([x, y, z], i) => {
      positions.set([x, y, z], i * 3);
      const c = rand() < 0.8 ? red : white;
      colors.set([c.r, c.g, c.b], i * 3);
      data.set([rand() * 10, 0.6 + rand() * 0.8, 0, 0], i * 4);
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aData', new THREE.BufferAttribute(data, 4));
    const beaconPoints = new THREE.Points(geometry, pointsMaterial({ uTime: this.uniforms.uTime }, 22, /* glsl */`
      float blink = fract(uTime * aData.y + aData.x);
      alpha = smoothstep(0.0, 0.05, blink) * (1.0 - smoothstep(0.25, 0.45, blink));
    `, '1.6'));
    beaconPoints.frustumCulled = false;
    this.scene.add(beaconPoints);
  }

  // Streams of flying traffic circling the city, animated entirely on the GPU.
  createTraffic() {
    const rand = seededRandom(99);
    const streams = 12;
    const convoys = 12;
    const perConvoy = 6;
    const perStream = convoys * perConvoy;
    const count = streams * perStream;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const data = new Float32Array(count * 4);
    const head = new THREE.Color(0xfff2d0);
    const tail = new THREE.Color(0xff4a5a);
    const cyan = new THREE.Color(0x7fe8ff);
    let i = 0;
    for (let s = 0; s < streams; s++) {
      const radius = CITY_INNER + 20 + rand() * (CITY_OUTER - CITY_INNER - 60);
      const height = 25 + rand() * 60;
      const direction = s % 2 ? 1 : -1;
      const speed = direction * (14 + rand() * 10) / radius;
      const color = s % 3 === 0 ? cyan : direction > 0 ? head : tail;
      // Vehicles travel in short convoys so each stream reads as moving dashes of light
      for (let k = 0; k < convoys; k++) {
        const start = rand() * Math.PI * 2;
        const lane = height + (rand() - 0.5) * 6;
        for (let v = 0; v < perConvoy; v++) {
          data.set([start + (v * 3.2) / radius, speed, radius, lane], i * 4);
          colors.set([color.r, color.g, color.b], i * 3);
          i++;
        }
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aData', new THREE.BufferAttribute(data, 4));
    const traffic = new THREE.Points(geometry, pointsMaterial({ uTime: this.uniforms.uTime }, 9, /* glsl */`
      float a = aData.x + uTime * aData.y;
      p = vec3(cos(a) * aData.z, aData.w, sin(a) * aData.z);
    `, '0.9'));
    traffic.frustumCulled = false;
    this.scene.add(traffic);
  }

  // Slow airships drifting high over the plain, with running lights.
  createAirships() {
    const rand = seededRandom(5150);
    const hullParts = [
      colored(new THREE.SphereGeometry(1, 12, 8), 0x2a3140, { scale: [3.2, 1.0, 12] }),
      colored(new THREE.BoxGeometry(5, 1.4, 10), 0x1d222d, { y: -0.9, z: -1 }),
      colored(new THREE.BoxGeometry(14, 0.3, 3), 0x3a4252, { z: -4 }),
      colored(new THREE.BoxGeometry(0.3, 4, 3), 0x3a4252, { y: 2, z: -9 })
    ];
    const hull = mergeGeometries(hullParts);
    const lightParts = mergeGeometries([
      colored(new THREE.BoxGeometry(0.15, 0.25, 8), 0xffd9a0, { x: 2.9, y: 0.2, z: 0.5 }),
      colored(new THREE.BoxGeometry(0.15, 0.25, 8), 0xffd9a0, { x: -2.9, y: 0.2, z: 0.5 }),
      colored(new THREE.CylinderGeometry(1.1, 1.1, 0.4, 10), 0x5fd8ff, { rx: Math.PI / 2, x: 1.6, y: -0.6, z: -11.5 }),
      colored(new THREE.CylinderGeometry(1.1, 1.1, 0.4, 10), 0x5fd8ff, { rx: Math.PI / 2, x: -1.6, y: -0.6, z: -11.5 }),
      colored(new THREE.BoxGeometry(0.5, 0.5, 0.5), 0xff3030, { x: -7, z: -4 }),
      colored(new THREE.BoxGeometry(0.5, 0.5, 0.5), 0x30ff70, { x: 7, z: -4 })
    ]);
    const hullMaterial = metal(0xffffff, { vertexColors: true });
    const lightMaterial = glow(0xffffff, { vertexColors: true, boost: 1.5 });

    for (let i = 0; i < 7; i++) {
      const ship = new THREE.Group();
      ship.add(new THREE.Mesh(hull, hullMaterial));
      ship.add(new THREE.Mesh(lightParts, lightMaterial));
      const engine = halo(0x5fd8ff, 9, 0.7);
      engine.position.set(0, -0.6, -12.5);
      ship.add(engine);
      const scale = 1.2 + rand() * 1.8;
      ship.scale.setScalar(scale);
      ship.userData = {
        radius: 220 + rand() * 300,
        angle: rand() * Math.PI * 2,
        height: 80 + rand() * 90,
        speed: (rand() < 0.5 ? -1 : 1) * (5 + rand() * 6),
        wobble: rand() * 10
      };
      this.scene.add(ship);
      this.airships.push(ship);
    }
  }

  // Force-field barrier walls on the same footprint as the old boundary,
  // with reactor pylons at the corners.
  createWalls() {
    this.fieldMaterial = new THREE.ShaderMaterial({
      uniforms: { uTime: this.uniforms.uTime, uColor: { value: new THREE.Color(0x3fc8ff) } },
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */`
        varying vec3 vWorld;
        varying vec2 vUv;
        void main() {
          vUv = uv;
          vWorld = (modelMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * viewMatrix * vec4(vWorld, 1.0);
        }
      `,
      fragmentShader: /* glsl */`
        uniform float uTime;
        uniform vec3 uColor;
        varying vec3 vWorld;
        varying vec2 vUv;
        float hexEdge(vec2 p) {
          const vec2 r = vec2(1.0, 1.7320508);
          vec2 h = r * 0.5;
          vec2 a = mod(p, r) - h;
          vec2 b = mod(p - h, r) - h;
          vec2 g = dot(a, a) < dot(b, b) ? a : b;
          vec2 q = abs(g);
          return 0.5 - max(dot(q, vec2(0.5, 0.8660254)), q.x);
        }
        void main() {
          float along = vWorld.x + vWorld.z;
          float y = vUv.y;
          float hex = 1.0 - smoothstep(0.0, 0.06, hexEdge(vec2(along, vWorld.y) / 1.2));
          float shimmer = 0.6 + 0.4 * sin(along * 0.35 - uTime * 2.0 + vWorld.y * 0.8);
          float scan = smoothstep(0.0, 0.1, fract(y - uTime * 0.25)) * (1.0 - smoothstep(0.1, 0.2, fract(y - uTime * 0.25)));
          float base = 1.0 - smoothstep(0.0, 0.9, y);
          float top = smoothstep(0.93, 1.0, y) * 0.6;
          float a = base * 0.35 + hex * shimmer * (0.18 + base * 0.3) * (1.0 - y * 0.6) + scan * 0.18 + top;
          gl_FragColor = vec4(uColor * a, 1.0);
        }
      `
    });

    const sides = [
      { id: 'boundary_n', position: { x: 0, y: 0, z: -HALF - WALL_THICKNESS / 2 }, size: { x: ARENA_SIZE + 3, y: WALL_HEIGHT, z: WALL_THICKNESS } },
      { id: 'boundary_s', position: { x: 0, y: 0, z: HALF + WALL_THICKNESS / 2 }, size: { x: ARENA_SIZE + 3, y: WALL_HEIGHT, z: WALL_THICKNESS } },
      { id: 'boundary_e', position: { x: HALF + WALL_THICKNESS / 2, y: 0, z: 0 }, size: { x: WALL_THICKNESS, y: WALL_HEIGHT, z: ARENA_SIZE } },
      { id: 'boundary_w', position: { x: -HALF - WALL_THICKNESS / 2, y: 0, z: 0 }, size: { x: WALL_THICKNESS, y: WALL_HEIGHT, z: ARENA_SIZE } }
    ];
    for (const side of sides) {
      this.boundary.push(new BarrierWall(this.scene, { ...side, type: 'wall', rotation: 0 }, this.fieldMaterial));
    }

    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      this.boundary.push(new Obstacle(this.scene, {
        id: `pylon_${x}_${z}`,
        type: 'pillar',
        position: { x: x * (HALF + 1), y: 0, z: z * (HALF + 1) },
        size: { x: 6, y: 11, z: 6 },
        rotation: 0
      }));
    }
  }

  // Faint motes of light drifting over the deck.
  createMotes() {
    const rand = this.rand;
    const count = 240;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const data = new Float32Array(count * 4);
    const a = new THREE.Color(SCIFI.cyan);
    const b = new THREE.Color(SCIFI.violet);
    for (let i = 0; i < count; i++) {
      positions.set([(rand() - 0.5) * (ARENA_SIZE + 60), 0.8 + rand() * 7, (rand() - 0.5) * (ARENA_SIZE + 60)], i * 3);
      const c = a.clone().lerp(b, rand());
      colors.set([c.r, c.g, c.b], i * 3);
      data.set([rand() * 100, 0.3 + rand() * 0.5, 0, 0], i * 4);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('aData', new THREE.BufferAttribute(data, 4));
    const motes = new THREE.Points(geometry, pointsMaterial({ uTime: this.uniforms.uTime }, 0.9, /* glsl */`
      float t = uTime * aData.y + aData.x;
      p += vec3(sin(t) * 2.5, sin(t * 1.7) * 0.8, cos(t * 0.8) * 2.5);
      alpha = 0.35 + 0.35 * sin(t * 2.3);
    `));
    motes.frustumCulled = false;
    this.scene.add(motes);
  }

  // Called each frame with the slow ambient cycle from the Scene.
  update(deltaTime, elapsed, ambient, focus) {
    this.uniforms.uTime.value = elapsed;

    if (ambient) {
      this.skyUniforms.aurora.value = ambient.aurora;
      this.skyUniforms.hueShift.value = ambient.hueShift;
      this.skyUniforms.moonDirection.value.copy(ambient.moonDirection);
    }

    // Sky and planet follow the camera so they never clip or parallax
    if (focus) {
      for (const { object, offset } of this.followers) {
        object.position.set(focus.x + offset.x, offset.y, focus.z + offset.z);
      }
    }

    for (const ship of this.airships) {
      const u = ship.userData;
      const a = u.angle + (elapsed * u.speed) / u.radius;
      ship.position.set(Math.cos(a) * u.radius, u.height + Math.sin(elapsed * 0.3 + u.wobble) * 3, Math.sin(a) * u.radius);
      // Face along the direction of travel
      ship.rotation.set(0, -a + (u.speed > 0 ? 0 : Math.PI), Math.sin(elapsed * 0.4 + u.wobble) * 0.03);
    }

    for (const piece of this.boundary) piece.update(deltaTime, elapsed);
  }

  getOccluders() {
    return this.boundary;
  }
}
